import React, { useState, useEffect } from 'react';
import "../../css/Parents.css";
import Loader from "../../components/Loader";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaSearch, FaUserFriends, FaFilter } from 'react-icons/fa';

function StudentsParents() {
    const [parents, setParents] = useState([]);
    const [filteredParents, setFilteredParents] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterOption, setFilterOption] = useState('all');
    const [showFilters, setShowFilters] = useState(false);

    useEffect(() => {
        fetchParents();
    }, []);

    const fetchParents = async () => {
        const token = localStorage.getItem("token");
        if (!token) {
            toast.error("Unauthorized! Please log in again.");
            return;
        }

        setIsLoading(true);

        try {
            const response = await fetch("https://schoolpro-api.uniums.com/api/teacher/parents", {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to fetch parents");
            }

            const data = await response.json();
            setParents(data);
            setFilteredParents(data);
        } catch (error) {
            console.error("Error fetching parents:", error);
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    // Handle search and filtering
    useEffect(() => {
        let results = parents;

        if (searchTerm) {
            results = results.filter(parent =>
                (parent.firstName?.toString().toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
                (parent.lastName?.toString().toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
                (parent.userCode?.toString() || "").includes(searchTerm) ||
                (parent.relationship?.toString().toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
                (parent.email?.toString().toLowerCase() || "").includes(searchTerm.toLowerCase())
            );
        }

        if (filterOption !== 'all') {
            results = results.filter(parent =>
                parent.relationship?.toString().toLowerCase() === filterOption.toLowerCase()
            );
        }

        setFilteredParents(results);
    }, [searchTerm, filterOption, parents]);

    // Get unique relationships for filter dropdown
    const relationships = [...new Set(parents.map(parent => parent.relationship?.toString()))]
        .filter(Boolean)
        .sort();

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleFilterChange = (e) => {
        setFilterOption(e.target.value);
    };

    const toggleFilters = () => {
        setShowFilters(!showFilters);
    };

    // Format date safely
    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        try {
            return new Date(dateString).toLocaleDateString();
        } catch (e) {
            return dateString;
        }
    };

    return (
        <div className="parents-container">
            <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} />

            {isLoading && <Loader />}

            <div className="parents-header">
                <div className="parents-title-container">
                    <FaUserFriends className="parents-icon" />
                    <h1 className="parents-title">Parent Directory</h1>
                </div>

                <div className="parents-controls">
                    <div className="parents-search-container">
                        <FaSearch className="parents-search-icon" />
                        <input
                            type="text"
                            className="parents-search-input"
                            placeholder="Search by name, user code, relationship..."
                            value={searchTerm}
                            onChange={handleSearch}
                        />
                    </div>

                    <button className="parents-filter-toggle-btn" onClick={toggleFilters}>
                        <FaFilter /> Filters
                    </button>
                </div>

                {showFilters && (
                    <div className="parents-filter-options">
                        <select
                            value={filterOption}
                            onChange={handleFilterChange}
                            className="parents-filter-select"
                        >
                            <option value="all">All Parents</option>
                            {relationships.map(relationship => (
                                <option key={relationship} value={relationship}>{relationship}</option>
                            ))}
                        </select>
                    </div>
                )}

                <div className="parents-stats">
                    <div className="parents-stat-box">
                        <span className="parents-stat-number">{filteredParents.length}</span>
                        <span className="parents-stat-label">Total Parents</span>
                    </div>
                    {filterOption !== 'all' && (
                        <div className="parents-stat-box highlight">
                            <span className="parents-stat-number">{filterOption}</span>
                            <span className="parents-stat-label">Relationship</span>
                        </div>
                    )}
                </div>
            </div>

            {filteredParents.length === 0 && !isLoading ? (
                <div className="parents-empty">
                    <div className="parents-empty-icon">
                        <FaSearch size={48} color="#a0aec0" />
                    </div>
                    <p className="parents-empty-text">No parents found matching your criteria.</p>
                    {searchTerm && (
                        <button className="parents-reset-search" onClick={() => setSearchTerm('')}>
                            Clear Search
                        </button>
                    )}
                </div>
            ) : (
                <div className="parents-grid">
                    {filteredParents.map((parent, index) => {
                        const firstInitial = parent.firstName ? parent.firstName.charAt(0) : '';
                        const lastInitial = parent.lastName ? parent.lastName.charAt(0) : '';
                        const relationship = parent.relationship?.toString().toLowerCase() || 'other';

                        return (
                            <div key={index} className="parents-card">
                                <div className={`parents-card-header ${relationship === 'mother' ? 'female' : 'male'}`}>
                                    <div className="parents-avatar">
                                        {firstInitial}{lastInitial}
                                    </div>
                                    <div className="parents-header-info">
                                        <h2 className="parents-name">
                                            {parent.firstName || ''} {parent.lastName || ''}
                                        </h2>
                                        <span className="parents-position">
                                            {parent.relationship || 'Parent'}
                                        </span>
                                    </div>
                                </div>

                                <div className="parents-card-body">
                                    <div className="parents-quick-info">
                                        <span className="parents-relationship-badge">{parent.relationship || 'N/A'}</span>
                                        <span className="parents-id-badge">Code: {parent.userCode || 'N/A'}</span>
                                    </div>

                                    <div className="parents-info-section">
                                        <h3 className="parents-section-title">Parent Info</h3>
                                        <div className="parents-info-group">
                                            <span className="parents-info-label">Student:</span>
                                            <span className="parents-info-value">{parent.student?.firstName} {parent.student?.lastName || 'N/A'}</span>
                                        </div>
                                        <div className="parents-info-group">
                                            <span className="parents-info-label">Occupation:</span>
                                            <span className="parents-info-value">{parent.occupation || 'N/A'}</span>
                                        </div>
                                    </div>

                                    <div className="parents-info-section">
                                        <h3 className="parents-section-title">Contact Info</h3>
                                        <div className="parents-info-group">
                                            <span className="parents-info-label">Email:</span>
                                            <span className="parents-info-value">{parent.email || 'N/A'}</span>
                                        </div>
                                        <div className="parents-info-group">
                                            <span className="parents-info-label">Phone:</span>
                                            <span className="parents-info-value">{parent.phone || 'N/A'}</span>
                                        </div>
                                    </div>

                                    <div className="parents-info-section">
                                        <h3 className="parents-section-title">Address</h3>
                                        <div className="parents-info-group parents-address">
                                            <span className="parents-info-value">{parent.address || 'N/A'}</span>
                                        </div>
                                    </div>

                                    <div className="parents-info-group parents-created-date">
                                        <span className="parents-info-label">Added:</span>
                                        <span className="parents-info-value">{formatDate(parent.createdAt)}</span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export default StudentsParents;